import { Product } from 'src/api/@generated/backend';

import { DataLayerStaticData } from 'src/static/data-layer-static-data';
import {
  CustomerAccountDataLayer,
  CustomerCityDataLayer,
  DataLayerEvent,
  DataLayerEventObject,
  EcomErrorDataLayer,
  DataLayerProduct,
  ProductInformationDataLayer,
  PurchaseInformationDataLayer,
} from 'src/types/data-layer-event-object';

class DataLayerUtils {
  // FIXME add customer_id and user_id from ubi secure token when its available
  public static readonly setCustomerAccountDataLayer = (numberOfAccounts: number, partnerId: string): void => {
    const customerAccount: CustomerAccountDataLayer = {
      event: DataLayerEvent.CUSTOMER_ACCOUNTS,
      account_count: numberOfAccounts,
      logged_in: true,
      partner_id: `${partnerId}`,
      customer_id: '',
      user_id: '',
    };

    DataLayerUtils.pushToDataLayer(customerAccount);
  };

  // FIXME add customer_id and user_id from ubi secure token when its available
  public static readonly setCustomerCityDataLayer = (zipCode: string, partnerId: number): void => {
    const customerCity: CustomerCityDataLayer = {
      event: DataLayerEvent.CUSTOMER_CITY,
      zip_code: zipCode,
      logged_in: true,
      partner_id: `${partnerId}`,
      customer_id: '',
      user_id: '',
    };

    DataLayerUtils.pushToDataLayer(customerCity);
  };

  public static readonly setEcomErrorDataLayer = (errorMessage: string, partnerId?: string, product?: Product) => {
    const ecomError: EcomErrorDataLayer = {
      event: DataLayerEvent.ECOM_ERROR,
      logged_in: true,
      partner_id: partnerId ? `${partnerId}` : undefined,
      customer_id: '',
      user_id: '',
      error_message: errorMessage,
      error_product: product?.name || '',
    };

    DataLayerUtils.pushToDataLayer(ecomError);
  };

  public static readonly setPurchaseDataLayer = (orderId: string, product: Product, index: number) => {
    window.dataLayer.push({ ecommerce: null });

    const purchaseInformation: PurchaseInformationDataLayer = {
      event: DataLayerEvent.PURCHASE,
      ecommerce: {
        transaction_id: orderId,
        value: DataLayerUtils.getPrice(product),
        currency: DataLayerStaticData.CURRENCY,
        items: [DataLayerUtils.toDataLayerProduct(product, index)],
      },
    };

    DataLayerUtils.pushToDataLayer(purchaseInformation);
  };

  public static readonly pushToProductsDataLayer = (event: DataLayerEvent, products: Product[]): void => {
    window.dataLayer.push({ ecommerce: null });

    const productsInformation: ProductInformationDataLayer = {
      event,
      ecommerce: {
        items: DataLayerUtils.toDataLayerProducts(products),
      },
    };

    DataLayerUtils.pushToDataLayer(productsInformation);
  };

  public static readonly pushToProductDataLayer = (event: DataLayerEvent, product: Product, index: number): void => {
    window.dataLayer.push({ ecommerce: null });

    const productInformation: ProductInformationDataLayer = {
      event,
      ecommerce: {
        items: [DataLayerUtils.toDataLayerProduct(product, index)],
      },
    };

    DataLayerUtils.pushToDataLayer(productInformation);
  };

  private static pushToDataLayer = (dataLayerObject: DataLayerEventObject): void => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(dataLayerObject);
  };

  private static readonly toDataLayerProducts = (products: Product[]): DataLayerProduct[] =>
    products.map((product, index) => DataLayerUtils.toDataLayerProduct(product, index));

  private static readonly toDataLayerProduct = (product: Product, index: number): DataLayerProduct => ({
    item_id: product.key,
    item_name: product.name,
    currency: DataLayerStaticData.CURRENCY,
    index,
    item_brand: DataLayerStaticData.PRODUCT_BRAND,
    item_category: DataLayerStaticData.HARDWARE_CATEGORY,
    item_category2: DataLayerStaticData.GAMING_CATEGORY,
    item_category3: DataLayerStaticData.XBOX_CATEGORY,
    item_category4: '',
    item_category5: '',
    price: DataLayerUtils.getPrice(product),
    item_list_id: [],
    item_list_name: [],
    quantity: DataLayerStaticData.PRODUCT_QUANTITY,
  });

  private static readonly getPrice = (product: Product) =>
    product.pricing?.totalPriceForInstallmentPeriod
      ? product.pricing.totalPriceForInstallmentPeriod
      : product.pricing.monthlyPrice;
}

export { DataLayerUtils };
